import {inject, SecurityContext} from '@angular/core';
import {DomSanitizer} from '@angular/platform-browser';
import {Parser, Token, Tokens} from 'marked';
import {MarkedOptions, MarkedRenderer} from 'ngx-markdown';
import {environment} from '../../../environments/environment';
import {TipoFicheroService} from '../services/tipo-fichero.service';
import {rutaDocumentoCitado} from './ruta-documento-citado';

export function stripSensitiveParams(text: string): string {
  return text
    .replace(/[?&]chatApiKey=[^&\s)"\]]+/g, '')
    .replace(/[?&]authToken=[^&\s)"\]]+/g, '');
}

// Las credenciales del lector solo viajan a la app o a la API propia. El token se inyecta sobre
// el texto del markdown y una ruta compartida puede quedar dentro de otra URL
// (`https://ajeno/x(/shared/...)`): aquí se decide sobre la URL que marked ya ha delimitado.
function sinCredencialesDeOtroOrigen(href: string): string {
  if (!/[?&](?:authToken|chatApiKey)=/.test(href)) {
    return href;
  }
  try {
    const origen = new URL(href, window.location.origin).origin;
    if (origen === window.location.origin || origen === new URL(environment.apiUrl, window.location.origin).origin) {
      return href;
    }
  } catch {
    // Una URL que no se puede interpretar tampoco se lleva credenciales.
  }
  return stripSensitiveParams(href);
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function escapeAttribute(text: string): string {
  return escapeHtml(text).replace(/`/g, '&#96;');
}

// `mailto:` entra en el allowlist por el error de permisos del origen (LUNIAGPT-5008), que ofrece
// escribir al buzón de soporte cuando el despliegue lo tiene configurado. Es un esquema inerte: no
// ejecuta nada, y el href sigue pasando por escapeAttribute.
function safeHref(href: string): string {
  const trimmedHref = href.trim();
  if (/^(https?:\/\/|mailto:|\/(?!\/)|#)/i.test(trimmedHref)) {
    return sinCredencialesDeOtroOrigen(trimmedHref);
  }

  return '';
}

function safeImageHref(href: string): string {
  const trimmedHref = href.trim();
  if (/^blob:/i.test(trimmedHref)) {
    return trimmedHref;
  }

  return safeHref(trimmedHref);
}

function isSharedImageUrl(href: string): boolean {
  return href.includes('/imagen-adjunta-documento/') ||
    /\.(png|jpe?g|gif|webp|svg|bmp)(\?.*)?$/i.test(href);
}

function isSharedMediaStreamUrl(href: string): boolean {
  return /\/(?:api\/)?shared\/documentos\/[0-9a-fA-F-]+\/stream-media(?:-conversacion)?(?:[?#].*)?$/.test(href);
}

// Extension reconocible: punto + 1-6 alfanumericos con al menos una letra al final del
// nombre visible (permite .7z o .3gp pero descarta decimales tipo "24.6").
function tieneExtensionReconocible(nombre: string): boolean {
  return /\.(?=[a-zA-Z0-9]*[a-zA-Z])[a-zA-Z0-9]{1,6}$/.test(nombre.trim());
}

// Recuadro de color de la tarjeta: etiqueta de extension como texto si la hay,
// SVG del icono de la categoria como fallback (mismo patron que TarjetaFicheroComponent).
function recuadroTipo(claseColor: string, etiqueta: string, iconoPath: string): string {
  const contenido = etiqueta
    ? `<span class="font-bold text-[11px] leading-none tracking-tight uppercase">${escapeHtml(etiqueta)}</span>`
    : `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.6" stroke="currentColor" class="w-6 h-6"><path stroke-linecap="round" stroke-linejoin="round" d="${iconoPath}"/></svg>`;

  return `<span class="size-10 rounded-lg ${claseColor} flex items-center justify-center shrink-0">${contenido}</span>`;
}

function mediaKind(text: string, title?: string): 'audio' | 'video' {
  if (title) {
    if (/\baudio\b/i.test(title)) {
      return 'audio';
    }
    if (/\bvideo\b/i.test(title)) {
      return 'video';
    }
  }

  return /\baudio\b/i.test(text) ? 'audio' : 'video';
}

function tokenText(token: Token): string {
  if ('raw' in token && token.raw) {
    return token.raw;
  }

  if ('text' in token && typeof token.text === 'string') {
    return token.text;
  }

  return '';
}

export function markedOptionsFactory(): MarkedOptions {
  const renderer = new MarkedRenderer();
  const sanitizer = inject(DomSanitizer);
  const tipoFichero = inject(TipoFicheroService);
  renderer.link = (token: Tokens.Link) => {
    const { href, title, tokens } = token;
    const rawHref = href ?? '';
    const text = tokens.map(tokenText).join('');
    const cleanText = stripSensitiveParams(text);
    const titleText = title ?? undefined;
    const cleanTitle = titleText ? stripSensitiveParams(titleText) : '';
    const label = cleanText || cleanTitle;
    const trustedHref = safeHref(rawHref);

    if (!trustedHref) {
      return escapeHtml(label || rawHref);
    }

    if (isSharedImageUrl(trustedHref)) {
      const imageId = 'img-' + Math.random().toString(36).substring(2, 9);
      const fallbackSrc = 'assets/img/no-image.jpg';
      const caption = label || '';
      const escapedHref = escapeAttribute(trustedHref);
      const escapedText = escapeAttribute(cleanText);
      const escapedCaption = escapeHtml(caption);
      const imageRender = `<figure class="relative border border-gray-300 group shadow-xs overflow-hidden rounded-xl w-fit md-image">
        <img src="${escapedHref}" alt="${escapedText}" title="${escapedText}" id="${imageId}" class="block max-h-[400px] w-auto cursor-zoom-in" data-fallback-src="${fallbackSrc}" tabindex="0" role="button" aria-label="Ampliar imagen: ${escapedText}"/>
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"
          class="size-6 absolute top-2 right-2 text-black bg-gray-50 rounded-md p-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300 border border-gray-50">
          <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
        </svg>
        <figcaption class="img-caption text-center text-sm text-gray-600 mt-2">${escapedCaption}</figcaption>
      </figure>`;
      return imageRender;
    }

    if (isSharedMediaStreamUrl(trustedHref)) {
      const src = escapeAttribute(trustedHref);
      const kind = mediaKind(text, titleText);
      const nombreVisible = cleanText || (kind === 'audio' ? 'Audio' : 'Vídeo');
      const etiqueta = tieneExtensionReconocible(nombreVisible) ? tipoFichero.etiqueta(nombreVisible) : '';
      const cabecera = `<div class="flex items-center gap-3 mb-3">
          ${recuadroTipo(tipoFichero.claseColor(kind), etiqueta, tipoFichero.iconoPath(kind))}
          <span class="min-w-0 flex-1 text-sm font-semibold text-gray-900 truncate">${escapeHtml(nombreVisible)}</span>
        </div>`;

      if (kind === 'audio') {
        return `<figure class="shared-media-container shared-audio my-3 w-fit max-w-full bg-white border border-gray-200 rounded-xl shadow-xs p-3">
        ${cabecera}
        <audio controls class="shared-media shared-media-audio" src="${src}"></audio>
      </figure>`;
      }

      return `<figure class="shared-media-container shared-video my-3 w-fit max-w-full bg-white border border-gray-200 rounded-xl shadow-xs p-3">
        ${cabecera}
        <video controls class="shared-media shared-media-video" src="${src}"></video>
      </figure>`;
    }

    const rutaCitada = rutaDocumentoCitado(trustedHref);
    if (rutaCitada) {
      const displayText = label || 'Documento';
      const pagina = rutaCitada.page
        ? `<span class="shared-download-pagina shrink-0 rounded-md bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-600 tabular-nums group-hover:bg-indigo-50 group-hover:text-indigo-700 group-aria-[current=true]:bg-white group-aria-[current=true]:text-indigo-700 transition">Pág. ${rutaCitada.page}</span>`
        : '';
      const etiqueta = tieneExtensionReconocible(displayText) ? tipoFichero.etiqueta(displayText) : '';
      const categoria = etiqueta ? tipoFichero.categoria(displayText) : 'generico';
      const iconoPath = tipoFichero.iconoPath(etiqueta ? categoria : 'texto');

      return `<a href="${escapeAttribute(trustedHref)}" class="shared-download group flex items-center gap-3 my-2 w-fit max-w-full bg-white border border-gray-200 rounded-xl shadow-xs hover:shadow-md hover:border-gray-300 aria-[current=true]:border-indigo-300 aria-[current=true]:bg-indigo-50 focus-visible:outline-2 focus-visible:outline-offset-2 transition p-3 no-underline">
        ${recuadroTipo(tipoFichero.claseColor(categoria), etiqueta, iconoPath)}
        <span class="min-w-0 flex-1"><span class="shared-download-nombre block font-semibold text-gray-900 text-sm truncate">${escapeHtml(displayText)}</span></span>
        ${pagina}
      </a>`;
    }

    const displayText = (text === href) ? cleanText : text;
    let out = `<a href="${escapeAttribute(trustedHref)}"`;
    if (title) {
      out += ` title="${escapeAttribute(cleanTitle)}"`;
    }
    out += ` target="_blank" rel="noopener noreferrer">${escapeHtml(displayText)}</a>`;
    return out;
  };

  renderer.html = (token: Tokens.HTML) => escapeHtml(tokenText(token));

  renderer.table = (token: Tokens.Table) => {
    const { header, rows } = token;
    const tableId = 'table-' + Math.random().toString(36).substring(2, 9);

    const parseCellContent = (cell: Tokens.TableCell): string => {
      return Parser.parseInline(cell.tokens || [], {renderer});
    };

    const textoPlano = (cell: Tokens.TableCell | undefined): string =>
      (cell?.text ?? '').replace(/[*_~`]/g, '').trim();

    const esValorNeutro = (texto: string): boolean =>
      texto === '' || texto === '-' || texto === '—' || texto === '–' || /^n\/a$/i.test(texto);

    // Números con formato es-ES o en-US, moneda y porcentaje: 1.284 · 14,2 · 412,80 € · +18 %
    const esValorNumerico = (texto: string): boolean =>
      /^[+\-−±]?[€$£]?\s?\d{1,3}(?:[.,\s ]\d{3})*(?:[.,]\d+)?\s?(?:€|\$|£|%)?$/.test(texto);

    const esColumnaNumerica = (indice: number): boolean => {
      const valores = rows.map((fila) => textoPlano(fila[indice])).filter((t) => !esValorNeutro(t));
      return valores.length > 0 && valores.every(esValorNumerico);
    };

    // La alineación GFM explícita (|:---:|) manda; sin ella, columna numérica → derecha (los
    // decimales quedan en columna y las cifras se comparan de un vistazo), texto → izquierda.
    const alineaciones: ('left' | 'center' | 'right')[] = header.map(
      (_, i) => token.align?.[i] ?? (esColumnaNumerica(i) ? 'right' : 'left'),
    );
    const CLASES_ALINEACION: Record<'left' | 'center' | 'right', string> = {
      left: 'text-left',
      center: 'text-center',
      right: 'text-right',
    };

    // Tabla densa: zebra como separador de filas en lugar de líneas (seguir la fila con la
    // vista importa más que separar registros); tabla normal: solo líneas divide-y.
    const esDensa = header.length > 6;
    const claseFila = esDensa
      ? 'odd:bg-white even:bg-gray-50 hover:bg-gray-100'
      : 'hover:bg-gray-50';

    const headerHtml = '<tr>' + header.map((cell: Tokens.TableCell, i: number) =>
      `<th class="px-4 py-2.5 ${CLASES_ALINEACION[alineaciones[i] ?? 'left']} text-xs font-semibold uppercase tracking-wider text-gray-500">${parseCellContent(cell)}</th>`
    ).join('') + '</tr>';

    const rowsHtml = rows.map((row: Tokens.TableCell[]) => {
      const esFilaTotal = row.length > 0 && /^total(es)?\b/i.test(textoPlano(row[0]));
      const clasesTr = esFilaTotal
        ? `${claseFila}${esDensa ? '' : ' bg-gray-50'} font-semibold`
        : claseFila;
      const celdas = row.map((cell: Tokens.TableCell, i: number) => {
        const alineacion = alineaciones[i] ?? 'left';
        const esNumerica = alineacion === 'right';
        const color = esFilaTotal || i === 0 ? 'text-gray-900' : 'text-gray-700';
        const extras = esNumerica ? ' tabular-nums whitespace-nowrap' : '';
        const contenido = parseCellContent(cell);
        const contenidoFinal = esNumerica && contenido.trim() === ''
          ? '<span class="text-gray-400">—</span>'
          : contenido;
        return `<td class="px-4 py-2 ${CLASES_ALINEACION[alineacion]} align-top ${color}${extras}">${contenidoFinal}</td>`;
      }).join('');
      return `<tr class="${clasesTr}">${celdas}</tr>`;
    }).join('');

    const boton = `<div class="flex justify-end border-t border-gray-200 px-2 py-1">
                <button disabled onclick="descargarContenidoTabla('${tableId}')" class="boton-descargar-tabla inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-gray-400 hover:bg-indigo-50 hover:text-indigo-600 transition focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-4">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
                    </svg>
                    Descargar CSV
                </button>
            </div>`;

    const table = `
            <div class="border border-gray-200 rounded-lg my-3 overflow-x-auto bg-white">
                <table class="w-full text-sm" id="${tableId}">
                    <thead class="bg-gray-50 border-b border-gray-200">${headerHtml}</thead>
                    <tbody${esDensa ? '' : ' class="divide-y divide-gray-200"'}>${rowsHtml}</tbody>
                </table>
                ${boton}
            </div>`;

    return sanitizer.sanitize(SecurityContext.HTML, sanitizer.bypassSecurityTrustHtml(table)) || '';
  };

  renderer.image = (token: Tokens.Image) => {
    const { href, title, text } = token;
    const imageId = 'img-' + Math.random().toString(36).substring(2, 9);
    const fallbackSrc = 'assets/img/no-image.jpg';
    const cleanText = stripSensitiveParams(text || '');
    const cleanTitle = title ? stripSensitiveParams(title) : '';
    const caption = cleanText || cleanTitle || '';
    const trustedHref = safeImageHref(href);

    if (!trustedHref) {
      return escapeHtml(caption);
    }

    const escapedHref = escapeAttribute(trustedHref);
    const escapedText = escapeAttribute(cleanText);
    const imageRender = `<figure class="relative border border-gray-300 group shadow-xs overflow-hidden rounded-xl w-fit md-image">
    <img src="${escapedHref}" alt="${escapedText}" title="${escapedText}" id="${imageId}" class="block max-h-[400px] w-auto cursor-zoom-in" data-fallback-src="${fallbackSrc}" tabindex="0" role="button" aria-label="Ampliar imagen: ${escapedText}"/>
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"
      class="size-6 absolute top-2 right-2 text-black bg-gray-50 rounded-md p-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300 border border-gray-50">
      <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
    </svg>
    <figcaption class="img-caption text-center text-sm text-gray-600 mt-2">${escapeHtml(caption)}</figcaption>
  </figure>`;
    return imageRender;
  };

  return {
    renderer: renderer,
    gfm: true,
    breaks: true,
    pedantic: false,
  };
}
